import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, LogOut, LayoutDashboard, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Catalogue", to: "/catalogue" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isCustomer = user && user !== false && user.role !== "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-black/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-4">
          {/* Left: mobile menu + logo */}
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 -ml-2" data-testid="mobile-menu-btn" aria-label="Menu">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 bg-obsidian text-white border-r-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="mt-4 mb-8">
                  <img src="/logo-mark.png" alt="ChainSmart" className="h-11 w-auto" />
                </div>
                <nav className="flex flex-col gap-1">
                  {navLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className="py-3 px-2 text-lg border-b border-white/10 hover:text-gold transition-colors"
                      data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    >
                      {l.label}
                    </Link>
                  ))}
                </nav>

                <div className="mt-8 flex flex-col gap-3">
                  {isCustomer && (
                    <Link to="/cart" onClick={() => setOpen(false)} className="flex items-center gap-2 py-3 px-2 text-base border-b border-white/10 hover:text-gold transition-colors" data-testid="mobile-cart-link">
                      <ShoppingBag className="w-5 h-5" /> Cart {cart.count > 0 && `(${cart.count})`}
                    </Link>
                  )}
                  {user && user !== false ? (
                    <>
                      {user.role === "admin" ? (
                        <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 py-3 px-2 text-base hover:text-gold transition-colors" data-testid="mobile-admin-link">
                          <LayoutDashboard className="w-5 h-5" /> Admin Dashboard
                        </Link>
                      ) : (
                        <Link to="/account" onClick={() => setOpen(false)} className="flex items-center gap-2 py-3 px-2 text-base hover:text-gold transition-colors" data-testid="mobile-account-link">
                          <User className="w-5 h-5" /> My Account
                        </Link>
                      )}
                      <button onClick={() => { setOpen(false); handleLogout(); }} className="flex items-center gap-2 py-3 px-2 text-base text-left hover:text-gold transition-colors" data-testid="mobile-logout">
                        <LogOut className="w-5 h-5" /> Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setOpen(false)} className="w-full inline-flex items-center justify-center gap-2 bg-gold text-obsidian font-semibold py-3 rounded-sm" data-testid="mobile-login-btn">
                        <User className="w-4 h-4" /> Login
                      </Link>
                      <Link to="/register" onClick={() => setOpen(false)} className="w-full inline-flex items-center justify-center gap-2 border border-white/40 text-white font-semibold py-3 rounded-sm hover:bg-white hover:text-obsidian transition-colors" data-testid="mobile-register-btn">
                        Create Account
                      </Link>
                      <Link to="/admin/login" onClick={() => setOpen(false)} className="text-center text-xs text-white/50 mt-2 hover:text-gold transition-colors" data-testid="mobile-admin-login">
                        Admin Login
                      </Link>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            <Link to="/" className="flex items-center" data-testid="logo-link">
              <span className="inline-flex items-center bg-black rounded-lg px-3 py-1.5 ring-1 ring-gold/30 shadow-sm">
                <img src="/logo-mark.png" alt="ChainSmart" className="h-8 lg:h-10 w-auto" />
              </span>
            </Link>
          </div>

          {/* Center nav (desktop) */}
          <nav className="hidden lg:flex items-center gap-9">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-sm font-medium tracking-wide text-obsidian hover:text-gold-dark transition-colors"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            <Link to="/catalogue" className="p-2 hover:text-gold-dark transition-colors" data-testid="search-icon" aria-label="Search">
              <Search className="w-5 h-5" />
            </Link>

            {isCustomer && (
              <Link to="/cart" className="relative p-2 hover:text-gold-dark transition-colors" data-testid="cart-icon" aria-label="Cart">
                <ShoppingBag className="w-5 h-5" />
                {cart.count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-gold text-obsidian text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-testid="cart-count">
                    {cart.count}
                  </span>
                )}
              </Link>
            )}

            {user && user !== false ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 hover:text-gold-dark transition-colors" data-testid="account-menu-btn" aria-label="Account">
                    <User className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5 text-sm font-medium truncate">{user.name}</div>
                  <DropdownMenuSeparator />
                  {user.role === "admin" ? (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Dashboard
                    </DropdownMenuItem>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                        <User className="w-4 h-4 mr-2" /> My Account
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/account?tab=orders")} data-testid="menu-orders">
                        <ShoppingBag className="w-4 h-4 mr-2" /> My Orders
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold bg-obsidian text-white hover:bg-gold hover:text-obsidian transition-colors rounded-sm"
                data-testid="login-nav-btn"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
